import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SizeDropdown } from '../components/SizeDropdown';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { VariantStatus } from '../enums/VariantStatus.enum';
import { Direction, useProductAssets } from '../hooks/useProductAssets';
import { useProductPrice } from '../hooks/useProductPrice';
import { useProductSelection } from '../hooks/useProductSelection';
import { useProductAvailability } from '../hooks/useVariantAvailability';
import { SizingToolState } from '../models/actionStates';
import { ViewProduct } from '../models/ViewProduct.model';
import ProductFormButton from '../product_detail/components/ProductFormButton';
import { LongSize } from '../product_detail/components/size.enum';
import { sizingToolState } from '../recoil/selectors';
import { Button } from '../root/components/buttons/Button';
import { Link } from '../root/components/Link';
import { sizeImage } from '../util';
import styles from './CompleteTheLookQuickBuy.module.scss';

interface Props {
  product: ViewProduct;
  initialSize: LongSize | undefined;
  color: Color;
  selectionKey: string;
}

export const CompleteTheLookQuickBuy: React.FC<Props> = ({ product, color, initialSize, selectionKey }) => {
  const { title } = product;
  const assets = useProductAssets(product);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const productAvailability = useProductAvailability(product);
  const { initializeColor, initializeFit, size, initializeSize, setSize } = useProductSelection(
    SelectionType.CompleteTheLook,
    selectionKey,
    product.handle,
    color,
  );

  const resetCompleteTheLookState = useCallback(() => {
    initializeColor(color);
    initializeSize(undefined);
    initializeFit(Fit.Regular);
  }, [initializeColor, initializeSize, initializeFit, color]);

  const openSizingTool = useCallback(() => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType: SelectionType.CompleteTheLook,
      selectionKey,
      initialProduct: product,
      product: product,
    });
  }, [product, setSizingToolState, selectionKey]);

  useEffect(() => {
    resetCompleteTheLookState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featuredImage = assets.mannequinPhotos.find((p) => p.color === color && p.direction === Direction.Front)?.src;

  const sizes = useMemo(() => {
    const availableVariants = productAvailability.filter((v) => v.color === color).filter((v) => v.status === VariantStatus.Available);
    return Array.from(new Set(availableVariants.map((av) => av.size as LongSize)));
  }, [color, productAvailability]);

  const cleanDisplayPrice = useProductPrice(product);

  return (
    <div className={styles.container}>
      <div>
        {featuredImage && (
          <Link dataTrackLabel="Complete The Look Navigation" dataTrackLocation="Complete The Look Navigation" href={`/products/${product.handle}`}>
            <img alt="Featured Product" src={sizeImage(featuredImage, 'x200')} />
          </Link>
        )}
      </div>
      <div className={styles.controls}>
        <span className={styles.title}>{title}</span>
        <div className={styles.colorPrice}>
          <span className={styles.color}>Color: {color}</span>
          <span className={styles.price}>{cleanDisplayPrice}</span>
        </div>
        <Button
          dataTrackLabel={'Sizing Help'}
          dataTrackLocation="Complete the Look"
          onClick={openSizingTool}
          buttonType="link"
          text={'SIZING HELP'}
          noMargin={true}
          labelStyleOptions={{ underline: true }}
        />
        <SizeDropdown
          type="external"
          setValue={setSize}
          value={size}
          availableValues={sizes}
          style={{
            marginBottom: 10,
            backgroundColor: 'white',
            border: '1px solid black',
            borderRadius: 3,
            height: 30,
            marginTop: 20,
            padding: 0,
            paddingLeft: 6,
          }}
          label="Size"
          allValues={product.sizes}
        />
        <ProductFormButton
          buttonType="secondary"
          selectionType={SelectionType.CompleteTheLook}
          selectionKey={selectionKey}
          text={'ADD TO BAG'}
          disablePreCartModal={true}
          quantity={1}
          forceStepper={product.fits?.length > 0}
        />
      </div>
    </div>
  );
};

export default CompleteTheLookQuickBuy;
